import { Component, OnDestroy, OnInit } from '@angular/core';

import { Store, select } from '@ngrx/store';
import { Observable, Subject, forkJoin } from 'rxjs';
import { switchMap, takeUntil, tap } from 'rxjs/operators';

import { SetActiveAccountId, SetActiveWorkspaceId } from '../../../services/active/active.actions';
import { SetBasicFetched } from '../../sync/sync.actions';
import { TUISettings } from '../../ui/ui.model';

import { Title } from '@angular/platform-browser';
import { TSidePanel } from 'src/app/project/modules/layout/side-panel/side-panel.model';
import { EInfiniteScrollApiKey } from 'src/app/project/services/infinite-scrolling/infinite-scrolling.consts';
import { EStore } from 'src/app/project/shared/enums/store.enum';
import { AccountService } from '../../account/account-service/account.service';
import { SidePanelService } from '../../layout/side-panel/side-panel.service';
import { PlanService } from '../../plan/plan.service';
import { EditPointService } from '../../points/point-full-modal/edit-point.service';
import { PointSearchService } from '../../points/point-search/point-search.service';
import { PreferencesService } from '../../preferences/preferences-service/preferences.service';
import { TSavedView } from '../../saved-views/models/saved-view.model';
import { SavedViewsService } from '../../saved-views/saved-views.service';
import { SyncService } from '../../sync/sync.service';
import { getSavedViewObservable } from '../../workspace/workspace-utils/get-saved-views-observable';
import { WorkspaceService } from '../../workspace/workspace.service';
import { EWorkspaces } from '../../workspace/workspaces.enum';
import { SiteDataService, TSiteData } from '../site-data.service';
import { TableLoadingService } from '../site-table/custom-table/table-loading.service';
import { SiteTableColumnsService } from '../site-table/site-table-columns.service';
import { SiteService } from '../site.service';

@Component({
  selector: 'pp-site-overview',
  templateUrl: './site-overview.component.html',
  styleUrls: ['./site-overview.component.scss'],
  standalone: false,
})
export class SiteOverviewComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  site: TSiteData = this.siteDataService.getSite();
  sidePanel: TSidePanel = this.sidePanelService.getSidePanel();

  ui$: Observable<TUISettings>;
  ui: TUISettings;

  tableCreated = false;
  dataFetched = false;
  isLoadingTable = false;

  constructor(
    private store: Store<{
      ui: TUISettings;
    }>,
    private siteDataService: SiteDataService,
    private sidePanelService: SidePanelService,
    private workspaceService: WorkspaceService,
    private syncService: SyncService,
    private preferencesService: PreferencesService,
    private accountService: AccountService,
    private siteTableColumnsService: SiteTableColumnsService,
    private editPointService: EditPointService,
    private siteService: SiteService,
    private titleService: Title,
    private savedViewsService: SavedViewsService,
    private pointSearchService: PointSearchService,
    private tableLoadingService: TableLoadingService,
    private planService: PlanService,
  ) {
    this.ui$ = this.store.pipe(select(EStore.UI));
    this.tableLoadingService.isTableLoading$
      .pipe(takeUntil(this.destroy$))
      .subscribe((isLoading) => {
        this.isLoadingTable = isLoading;
      });
  }

  ngOnInit() {
    this.titleService.setTitle('Loading... | Pinpoint Works');

    const workspaces = this.workspaceService.getWorkspaces();
    const accounts = this.accountService.getAccounts();
    this.store.dispatch(new SetActiveAccountId(null));
    this.sidePanelService.initSidePanel({ expandedInitially: false });

    if (!Array.isArray(accounts) || Object.entries(workspaces).length === 0) {
      this.store.dispatch(new SetActiveWorkspaceId(null));

      this.syncService
        .firstLoad()
        .pipe(
          takeUntil(this.destroy$),
          tap(() => {
            this.loadData();
          }),
        )
        .subscribe();
    } else {
      this.store.dispatch(new SetActiveWorkspaceId(null));

      this.loadData();
    }

    this.ui$.pipe(takeUntil(this.destroy$)).subscribe((ui) => {
      this.ui = ui;
    });
  }

  private loadData(): void {
    const requests: Observable<any>[] = [
      this.savedViewsService.fetchSavedViews(EWorkspaces.OVERVIEW),
      this.workspaceService.generateWorkspaces(),
      this.preferencesService.fetchPreferences(),
    ];

    this.pointSearchService.setAPIKey(EInfiniteScrollApiKey.OVERVIEW_POINTS);

    forkJoin(requests)
      .pipe(
        switchMap(([viewsResponse]) => {
          const savedViewsObservable: Observable<TSavedView> = getSavedViewObservable({
            savedViewsResponse: viewsResponse,
            savedViewsService: this.savedViewsService,
            workspaceId: EWorkspaces.OVERVIEW,
          });

          return savedViewsObservable;
        }),
        takeUntil(this.destroy$),
        tap(() => {
          this.onDataFetched();
        }),
      )
      .subscribe();
  }

  onDataFetched(): void {
    this.dataFetched = true;

    this.updateTitle();
    this.siteTableColumnsService.initAllColumns();

    this.siteService.setFetched(true);
    this.store.dispatch(new SetBasicFetched(true));
  }

  ngOnDestroy() {
    this.siteDataService.clearSite();
    this.editPointService.hideModal();
    this.planService.clearSitePlanCache();

    this.destroy$.next();
  }

  updateTitle(): void {
    this.titleService.setTitle('Overview | Pinpoint Works');
  }

  hidePlaceholder(): void {
    this.tableCreated = true;
  }
}
